import {
  effectivePermissionSetHas,
  LPS_TAB_ORDER,
  lpsTabReadCode,
  lpsTabWriteCode,
  type LeadListItem,
  type LeadListQuery,
  type LpsTabId,
} from '@utazzo/shared';
import { UzAvatar, UzBadge, UzTabs } from '@utazzo/ui';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { resolveStageTone, stageBadgeTone } from '../stage-color';
import { useLeadsList } from '../use-leads';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { LpsConnectionsTab } from '@/features/lead-parsing/components/LpsConnectionsTab';
import { LpsDeliveriesTab } from '@/features/lead-parsing/components/LpsDeliveriesTab';
import { LpsLeadsTab } from '@/features/lead-parsing/components/LpsLeadsTab';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { useQuerySourcesList } from '@/features/query-sources/use-query-sources';

const MIN_SEARCH_LENGTH = 3;

/**
 * Lead Parsing System, three tabs:
 *   - Inbound leads: everything the LPS connectors captured (lps_leads) and
 *     whether each one reached the CRM.
 *   - Connections: the LPS connectors, with their webhook URLs.
 *   - Website leads (CRM): the original view below.
 *
 * Website leads (CRM) tab:
 * Every lead captured by the public website forms (`POST
 * /website/forms/submit`), parsed into the two signals sales cares about:
 * where the visitor wants to go (destination) and which package page they
 * enquired from. Server-side the list is scoped with `websiteOnly=true`
 * (QuerySource.code starts with "website"); the package name is parsed
 * client-side out of the enquiry message because the website forms embed it
 * as free text ("Package enquiry: Bali Escape") rather than a structured FK.
 */

/// The website forms embed the package name in the message / title as free
/// text. Known shapes (see apps/website/components/**):
///   - PackageLeadForm   -> "Package enquiry: <name>"
///   - EnquiryPopupSeed  -> "Popup enquiry from itinerary: <name>"
///   - LandingPackages   -> "Customize package: <name>"  (landing page CTA)
const PACKAGE_MESSAGE_RE =
  /(?:package enquiry|popup enquiry from itinerary|customize package)\s*:\s*(.+)/i;

/// Fallback: derive a readable package name from the page URL when the
/// message carried nothing (e.g. ".../package-itinerary/bali-escape-5d").
const PACKAGE_URL_RE = /\/package(?:-itinerary|s)?\/([^/?#]+)/i;

export function parsePackageName(row: {
  notes?: string | null;
  title: string;
  querySourceUrl?: string | null;
}): string | null {
  for (const text of [row.notes, row.title]) {
    const m = text ? PACKAGE_MESSAGE_RE.exec(text) : null;
    if (m?.[1]) return m[1].trim();
  }
  const urlMatch = row.querySourceUrl ? PACKAGE_URL_RE.exec(row.querySourceUrl) : null;
  if (urlMatch?.[1]) {
    // Prettify the slug: "bali-escape-5d" -> "Bali Escape 5d".
    return urlMatch[1]
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return null;
}

export function resolveDestination(row: LeadListItem): string | null {
  return row.destination?.name ?? row.destinationCity?.name ?? row.destinationCityText ?? null;
}

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Strip protocol + host so long URLs stay readable in the grid. */
const shortenUrl = (url: string): string => {
  try {
    const u = new URL(url, 'https://x');
    return `${u.pathname}${u.search}` || url;
  } catch {
    return url;
  }
};

const WebsiteLeadsTab = (): React.JSX.Element => {
  // Source filter options: only the website-* query sources (the same set
  // the server's `websiteOnly` flag matches on), so the dropdown can't
  // select a source that would always return zero rows here.
  const sources = useQuerySourcesList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const filters: ReadonlyArray<DataGridFilter> = useMemo(() => {
    const websiteSources = (sources.data?.data ?? []).filter((s) => s.code.startsWith('website'));
    return [
      {
        id: 'querySourceId',
        label: 'Form',
        options: [
          { value: '', label: 'All website forms' },
          ...websiteSources.map((s) => ({ value: s.id, label: s.name })),
        ],
        widthClass: 'w-56',
      },
    ];
  }, [sources.data]);

  const columns: ListScreenColumns<LeadListItem> = useMemo(
    () =>
      ({ state }) => {
        const cols: ReadonlyArray<DataGridColumn<LeadListItem>> = [
          {
            id: 'serial',
            header: 'S.No',
            width: '64px',
            cell: (_row, index) => (
              <span className="text-zinc-500">
                {(state.page - 1) * state.pageSize + index + 1}.
              </span>
            ),
          },
          {
            id: 'customer',
            header: 'Customer',
            cell: (row) => (
              <div className="flex min-w-0 items-center gap-2.5">
                <UzAvatar name={row.customer.displayName} size="sm" />
                <span className="truncate text-sm font-medium text-zinc-900">
                  {row.customer.displayName}
                </span>
              </div>
            ),
          },
          {
            id: 'contact',
            header: 'Contact',
            cell: (row) => (
              <div className="flex flex-col text-xs">
                <span className="font-mono text-zinc-700">{row.customer.mobileNumber ?? '-'}</span>
                {row.customer.email && (
                  <span className="truncate text-zinc-500">{row.customer.email}</span>
                )}
              </div>
            ),
          },
          {
            id: 'destination',
            header: 'Destination',
            cell: (row) => {
              const destination = resolveDestination(row);
              return destination ? (
                <span className="text-sm text-zinc-900">{destination}</span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              );
            },
          },
          {
            id: 'package',
            header: 'Package',
            cell: (row) => {
              const pkg = parsePackageName(row);
              return pkg ? (
                <span className="text-sm text-zinc-900" title={row.notes ?? undefined}>
                  {pkg}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              );
            },
          },
          {
            id: 'source',
            header: 'Form',
            cell: (row) =>
              row.querySource ? (
                <UzBadge tone="secondary" pill>
                  {row.querySource.name}
                </UzBadge>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'page',
            header: 'Page',
            cell: (row) =>
              row.querySourceUrl ? (
                <span
                  className="block max-w-[220px] truncate font-mono text-[11px] text-zinc-500"
                  title={row.querySourceUrl}
                >
                  {shortenUrl(row.querySourceUrl)}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'message',
            header: 'Message',
            cell: (row) =>
              row.notes ? (
                <span
                  className="block max-w-[280px] truncate text-xs text-zinc-600"
                  title={row.notes}
                >
                  {row.notes}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'currentStatus',
            header: 'Stage',
            cell: (row) => (
              <UzBadge
                tone={stageBadgeTone(resolveStageTone(row.currentBucket, row.currentStatus.name))}
                pill
              >
                {row.currentStatus.name}
              </UzBadge>
            ),
          },
          {
            id: 'createdAt',
            header: 'Received',
            sortable: true,
            cell: (row) => (
              <span className="text-xs whitespace-nowrap text-zinc-700">
                {formatDateTime(row.createdAt)}
              </span>
            ),
          },
        ];
        return cols;
      },
    [],
  );

  return (
    <ListScreen<LeadListItem, LeadListQuery>
      embedded
      tableLayout="auto"
      title="Website leads"
      useList={useLeadsList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const { search, ...rest } = base;
        const q = search?.trim() ? search : undefined;
        const querySourceId = state.filters.querySourceId as string | undefined;
        return {
          ...rest,
          ...(q ? { q } : {}),
          websiteOnly: true,
          ...(querySourceId ? { querySourceId } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      disableRowClick
      copy={{
        searchPlaceholder: 'Search reference, customer, phone, email (min 3 characters)',
        emptyTitle: 'No website leads yet',
        emptyDescription:
          'Leads appear here as soon as a visitor submits any lead form on the website.',
        errorTitle: 'Failed to load website leads',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};

/**
 * Tabs taken off the strip for everyone, admins included. 2026-10-05: the
 * "Website leads (CRM)" tab is hidden - website leads already arrive in the
 * Inbound queue through LPS, so the old CRM-side view only duplicated them.
 * Its content and its permission module stay, so removing the id here brings
 * the tab back as it was.
 */
const HIDDEN_TABS: ReadonlySet<LpsTabId> = new Set<LpsTabId>(['website']);

const TAB_CONTENT: Record<
  LpsTabId,
  (canWrite: boolean) => { value: LpsTabId; label: string; content: React.JSX.Element }
> = {
  inbound: (canWrite) => ({
    value: 'inbound',
    label: 'Inbound leads',
    content: <LpsLeadsTab canWrite={canWrite} />,
  }),
  deliveries: () => ({ value: 'deliveries', label: 'Deliveries', content: <LpsDeliveriesTab /> }),
  connections: (canWrite) => ({
    value: 'connections',
    label: 'Connections',
    content: <LpsConnectionsTab canWrite={canWrite} />,
  }),
  website: () => ({ value: 'website', label: 'Website leads (CRM)', content: <WebsiteLeadsTab /> }),
};

export const LeadParsingPage = (): React.JSX.Element => {
  const permissions = useMyEffectivePermissions();
  const isAdmin = useCurrentUser()?.role === 'ADMIN';
  // 2026-10-02. One permission module per tab (LPS_TAB_MODULES), like the
  // Settings tabs: a role sees only the tabs it was granted, and writes
  // (retry / assign / connection changes) need that tab's write code.
  // `ops.lead_parsing.read` opens the page; it is each tab's prerequisite.
  const shown = LPS_TAB_ORDER.filter((t) => !HIDDEN_TABS.has(t));
  const visible: LpsTabId[] = isAdmin
    ? shown
    : shown.filter((t) => effectivePermissionSetHas(permissions, lpsTabReadCode(t)));
  const canWrite = (t: LpsTabId): boolean =>
    isAdmin || effectivePermissionSetHas(permissions, lpsTabWriteCode(t));
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: LpsTabId | null = visible.includes(requested as LpsTabId)
    ? (requested as LpsTabId)
    : (visible[0] ?? null);

  return (
    <PageShell
      title="Lead Parsing System"
      description="Leads from website forms, Meta lead ads, WhatsApp and landing pages, and where each one went."
    >
      <UzTabs
        value={tab ?? ''}
        onValueChange={(next) =>
          setParams(next === 'inbound' ? {} : { tab: next }, { replace: true })
        }
        // Phones: the four tabs don't fit, so the strip scrolls sideways.
        listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
        tabs={visible.map((t) => TAB_CONTENT[t](canWrite(t)))}
      />
    </PageShell>
  );
};
